// ============================================================================
// OMS Escalations — Kylas escalation tickets the escalation team has parked on
// procurement: Escalation pipeline, stage "Internal Dependency", Dependency
// "Passed to Procurement". Same card + drawer flow as the CRM's escalation tab.
// Notes and files are written straight to the Kylas deal (see kylas.api.ts).
// ============================================================================
import {
  AlertOutlined,
  ClockCircleOutlined,
  EditOutlined,
  ExportOutlined,
  FormOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Pagination,
  Segmented,
  Select,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchUserDetails } from "../../../apis/user.api";
import styles from "../index.module.scss";
import {
  addDealNote,
  cfDisplay,
  getDeal,
  KylasDeal,
  kylasConfigured,
  kylasDealUrl,
  KylasNote,
  isUpdateComplete,
  ProcurementUpdate,
  submitProcurementUpdate,
  listDealNotes,
  listDealTimeline,
  listProcurementEscalations,
  TimelineEntry,
  uploadDealFiles,
} from "./kylas.api";

type DateFilter = "today" | "7days" | "month" | "all";

const PAGE_SIZE = 20;

const DATE_OPTIONS: { value: DateFilter; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7days", label: "7 days" },
  { value: "month", label: "This month" },
  { value: "all", label: "All time" },
];

function dateFloor(f: DateFilter): number | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (f === "today") return today.getTime();
  if (f === "7days") return today.getTime() - 6 * 86400000;
  if (f === "month") return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  return null;
}

function relativeAge(iso?: string): string {
  if (!iso) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return hrs % 24 ? `${days}d ${hrs % 24}h` : `${days}d`;
}

const fmtDateTime = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";

type ActionType = "note" | "doc" | "update";

// ---------------------------------------------------------------------------
// Procurement update — the structured reply procurement owes the escalation
// team. Every field is required; saving posts a Kylas note and moves the ticket
// Internal Dependency → In Progress (handed back to escalation).
// ---------------------------------------------------------------------------

type UpdateFormValues = {
  items: { sku?: string; quantity?: number | null }[];
  leadTimeDays?: number | null;
  remarks?: string;
};

const toUpdate = (v: UpdateFormValues): ProcurementUpdate => ({
  items: (v.items ?? []).map((i) => ({ sku: i?.sku ?? "", quantity: i?.quantity ?? null })),
  leadTimeDays: v.leadTimeDays ?? null,
  remarks: v.remarks ?? "",
});

const ProcurementUpdateForm: React.FC<{
  dealId: number;
  author: string;
  onDone: (summary: string, moved: boolean) => void;
}> = ({ dealId, author, onDone }) => {
  const [form] = Form.useForm<UpdateFormValues>();
  const values = Form.useWatch([], form) as UpdateFormValues | undefined;
  const complete = values ? isUpdateComplete(toUpdate(values)) : false;
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const u = toUpdate(form.getFieldsValue(true));
    if (!isUpdateComplete(u)) {
      message.warning("Fill SKU & quantity, lead time and remarks before saving");
      return;
    }
    setSaving(true);
    try {
      const res = await submitProcurementUpdate(dealId, author, u);
      if (res.moved) message.success("Update saved to Kylas notes · ticket moved to In Progress");
      else if (res.moveError) message.warning(`Update saved to Kylas notes, but: ${res.moveError}`);
      else message.success("Update saved to Kylas notes");
      const items = u.items.filter((i) => i.sku.trim()).map((i) => `${i.sku.trim()} × ${i.quantity ?? "—"}`).join(", ");
      onDone(
        [items, u.leadTimeDays != null ? `lead time ${u.leadTimeDays}d` : "", u.remarks.trim()].filter(Boolean).join(" · "),
        res.moved
      );
      form.resetFields();
    } catch (e: any) {
      message.error(e?.message || "Failed to save update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form form={form} layout="vertical" initialValues={{ items: [{}] }} requiredMark={false} className={styles.escForm}>
      <div className={styles.escTicketLink}>
        <span>Kylas ticket</span>
        <Typography.Link href={kylasDealUrl(dealId)} target="_blank" className="text-decoration-none">
          #{dealId} <ExportOutlined />
        </Typography.Link>
        <Typography.Text copyable={{ text: kylasDealUrl(dealId), tooltips: ["Copy ticket link", "Copied"] }} />
      </div>

      <div className={styles.escLabel}>Item SKU &amp; Quantity</div>
      <Form.List name="items">
        {(fields, { add, remove }) => (
          <div className={styles.escItems}>
            {fields.map((f, idx) => (
              <div key={f.key} className={styles.escItemRow}>
                <Form.Item name={[f.name, "sku"]} noStyle>
                  <Input placeholder={`SKU ${fields.length > 1 ? idx + 1 : ""}`.trim()} />
                </Form.Item>
                <Form.Item name={[f.name, "quantity"]} noStyle>
                  <InputNumber placeholder="Qty" min={0} style={{ width: "100%" }} />
                </Form.Item>
                <Button
                  type="text"
                  icon={<MinusCircleOutlined />}
                  onClick={() => remove(f.name)}
                  disabled={fields.length === 1}
                  aria-label="Remove item"
                />
              </div>
            ))}
            <Button type="link" icon={<PlusOutlined />} onClick={() => add({})} style={{ paddingLeft: 0, alignSelf: "flex-start" }}>
              Add another item
            </Button>
          </div>
        )}
      </Form.List>

      <div className={styles.escFieldRow}>
        <Form.Item label="Updated lead time" name="leadTimeDays">
          <InputNumber min={0} precision={0} addonAfter="days" placeholder="e.g. 5" style={{ width: "100%" }} />
        </Form.Item>
      </div>
      <Form.Item label="Remarks" name="remarks">
        <Input.TextArea placeholder="What changed, vendor update, next steps…" autoSize={{ minRows: 3, maxRows: 6 }} />
      </Form.Item>

      <div className={styles.escFooter}>
        <Typography.Text type={complete ? "success" : "secondary"} style={{ fontSize: 12 }}>
          {complete ? "All fields filled — ticket moves to In Progress" : "Fill every field to save"}
        </Typography.Text>
        <Button type="primary" onClick={submit} loading={saving} disabled={!complete}>
          Save &amp; move to In Progress
        </Button>
      </div>
    </Form>
  );
};

// ---------------------------------------------------------------------------
// Note + upload composers, shared by the card and the drawer.
// ---------------------------------------------------------------------------

const NoteComposer: React.FC<{ dealId: number; author: string; onDone: (text: string) => void }> = ({
  dealId,
  author,
  onDone,
}) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addDealNote(dealId, author, text.trim());
      message.success("Note added to Kylas");
      onDone(text.trim());
      setText("");
    } catch (e: any) {
      message.error(e?.message || "Failed to add note");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div>
      <Input.TextArea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type a note…"
        autoSize={{ minRows: 2, maxRows: 6 }}
        onPressEnter={(e) => {
          if (e.metaKey || e.ctrlKey) save();
        }}
      />
      <div className={styles.escFooter}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          ⌘/Ctrl + Enter to save
        </Typography.Text>
        <Button type="primary" onClick={save} loading={saving} disabled={!text.trim()}>
          Save note
        </Button>
      </div>
    </div>
  );
};

const FileUploader: React.FC<{ dealId: number; author: string; onDone: (names: string) => void }> = ({
  dealId,
  author,
  onDone,
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const upload = async () => {
    const raw = files.map((f) => f.originFileObj as File).filter(Boolean);
    if (!raw.length) return;
    setUploading(true);
    try {
      await uploadDealFiles(dealId, author, raw);
      const names = raw.map((f) => f.name).join(", ");
      message.success("Uploaded to Kylas");
      onDone(names);
      setFiles([]);
    } catch (e: any) {
      message.error(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <Upload.Dragger
        multiple
        fileList={files}
        beforeUpload={() => false}
        onChange={({ fileList }) => setFiles(fileList)}
        disabled={uploading}
        className={styles.escDragger}
      >
        <UploadOutlined style={{ fontSize: 18, color: "#24AAB7" }} />
        <div style={{ fontSize: 13 }}>Click or drop files</div>
        <div style={{ fontSize: 11, color: "#8c8c8c" }}>Saved to the Kylas ticket&apos;s documents</div>
      </Upload.Dragger>
      <div className={styles.escFooter}>
        <span />
        <Button type="primary" onClick={upload} loading={uploading} disabled={!files.length}>
          Upload{files.length ? ` ${files.length} file${files.length > 1 ? "s" : ""}` : ""}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------

const EscalationDrawer: React.FC<{
  deal: KylasDeal | null;
  author: string;
  onClose: () => void;
  onAction: (dealId: number, type: ActionType, text: string, moved?: boolean) => void;
}> = ({ deal, author, onClose, onAction }) => {
  const [detail, setDetail] = useState<KylasDeal | null>(null);
  const [notes, setNotes] = useState<KylasNote[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [sideTab, setSideTab] = useState<"note" | "doc">("note");

  const loadNotes = useCallback(async (d: KylasDeal) => {
    setNotes(await listDealNotes(d.id, d.ownedBy?.id).catch(() => []));
  }, []);

  useEffect(() => {
    if (!deal) return;
    let cancelled = false;
    setDetail(null);
    setNotes([]);
    setTimeline([]);
    setLoading(true);
    (async () => {
      const d = await getDeal(deal.id).catch(() => deal);
      if (cancelled) return;
      setDetail(d);
      await loadNotes(d);
      if (cancelled) return;
      setLoading(false);
      const t = await listDealTimeline(deal.id).catch(() => []);
      if (!cancelled) setTimeline(t);
    })();
    return () => {
      cancelled = true;
    };
  }, [deal, loadNotes]);

  const d = detail ?? deal;
  const cf = d?.customFieldValues ?? {};
  const afterAction = (type: ActionType) => (text: string, moved?: boolean) => {
    if (!d) return;
    onAction(d.id, type, text, moved);
    if (!moved) loadNotes(d);
  };

  return (
    <Drawer
      open={!!deal}
      onClose={onClose}
      width={600}
      destroyOnClose
      // Title and the Kylas button share one flex row we own: antd's `.ant-drawer-title` has no
      // min-width 0, so with the button in `extra` a long deal name refused to shrink and ran under it.
      rootClassName={styles.escDrawer}
      title={
        d ? (
          <div className={styles.escDrawerHead}>
            <div className={styles.escDrawerTitle}>
              <Typography.Text strong ellipsis={{ tooltip: d.name }} style={{ fontSize: 15 }}>
                {d.name}
              </Typography.Text>
              <div className={styles.escDrawerMeta}>
                <span>{d.ownedBy?.name ?? "—"}</span>
                {d.pipelineStage?.name && <Tag color="gold" style={{ margin: 0 }}>{d.pipelineStage.name.trim()}</Tag>}
              </div>
            </div>
            <Button icon={<ExportOutlined />} href={kylasDealUrl(d.id)} target="_blank" className="text-decoration-none">
              Open in Kylas
            </Button>
          </div>
        ) : null
      }
    >
      {!d ? null : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {/* Collapsible so the notes/ticket details below are one click away; the form stays
              mounted when closed, so a half-filled update is not lost. */}
          <Collapse
            className={styles.escUpdateCollapse}
            defaultActiveKey={["update"]}
            expandIconPosition="end"
            items={[
              {
                key: "update",
                label: (
                  <span style={{ fontWeight: 600 }}>
                    <FormOutlined style={{ marginRight: 8, color: "#24AAB7" }} />
                    Procurement update
                  </span>
                ),
                children: <ProcurementUpdateForm dealId={d.id} author={author} onDone={afterAction("update")} />,
              },
            ]}
          />

          <Card
            size="small"
            className={styles.escCard}
            activeTabKey={sideTab}
            onTabChange={(k) => setSideTab(k as "note" | "doc")}
            tabList={[
              { key: "note", tab: <span><EditOutlined /> Add note</span> },
              { key: "doc", tab: <span><PaperClipOutlined /> Upload files</span> },
            ]}
          >
            {sideTab === "note" ? (
              <NoteComposer dealId={d.id} author={author} onDone={afterAction("note")} />
            ) : (
              <FileUploader dealId={d.id} author={author} onDone={afterAction("doc")} />
            )}
          </Card>

          <Descriptions size="small" column={1} bordered title="Ticket details" labelStyle={{ width: 130 }}>
            <Descriptions.Item label="Raised by">{(typeof cf.cfProjectPhase === "string" && cf.cfProjectPhase) || "—"}</Descriptions.Item>
            <Descriptions.Item label="Request type">{cfDisplay(cf.cfRequestType)}</Descriptions.Item>
            <Descriptions.Item label="Dependency">{cfDisplay(cf.cfDependency)}</Descriptions.Item>
            <Descriptions.Item label="RCA">{cfDisplay(cf.cfRcaEscalationReason)}</Descriptions.Item>
            <Descriptions.Item label="Resolution">{cfDisplay(cf.cfResolution)}</Descriptions.Item>
            <Descriptions.Item label="Refund/CN">{cfDisplay(cf.cfRefundCnAmount)}</Descriptions.Item>
            <Descriptions.Item label="Attribution">{cfDisplay(cf.cfEscalationClassification)}</Descriptions.Item>
            {typeof cf.cfNotes === "string" && cf.cfNotes && (
              <Descriptions.Item label="Ticket notes">
                <span style={{ whiteSpace: "pre-wrap" }}>{cf.cfNotes}</span>
              </Descriptions.Item>
            )}
          </Descriptions>

          <div>
            <Typography.Title level={5}>Notes ({notes.length})</Typography.Title>
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : notes.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes yet" />
            ) : (
              <List
                size="small"
                bordered
                dataSource={notes}
                renderItem={(n) => (
                  <List.Item>
                    <div style={{ width: "100%" }}>
                      <div style={{ whiteSpace: "pre-wrap" }}>{n.description}</div>
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
                        {relativeAge(n.createdAt)} ago · {fmtDateTime(n.createdAt)}
                        {n.createdBy ? ` · ${n.createdBy}` : ""}
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </div>

          {timeline.length > 0 && (
            <div>
              <Typography.Title level={5}>Timeline</Typography.Title>
              <Timeline
                items={timeline
                  .slice()
                  .reverse()
                  .map((t) => ({
                    children: (
                      <div>
                        <div style={{ fontWeight: 500 }}>{t.event}</div>
                        {t.description && <div style={{ fontSize: 12, color: "#595959" }}>{t.description}</div>}
                        <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                          {fmtDateTime(t.createdAt)} · {t.performedBy}
                        </div>
                      </div>
                    ),
                  }))}
              />
            </div>
          )}
        </Space>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const OmsEscalations: React.FC = () => {
  const [deals, setDeals] = useState<KylasDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<KylasDeal | null>(null);
  const [composer, setComposer] = useState<{ dealId: number; type: ActionType } | null>(null);
  const [lastAction, setLastAction] = useState<Record<number, { type: ActionType; text: string }>>({});
  const [author, setAuthor] = useState("Procurement");

  useEffect(() => {
    fetchUserDetails().then((u: any) => {
      const name = [u?.f_name, u?.l_name].filter(Boolean).join(" ").trim();
      if (name) setAuthor(`${name} (Procurement)`);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDeals(await listProcurementEscalations());
    } catch (e: any) {
      setError(e?.message || "Failed to load escalations from Kylas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (kylasConfigured()) load();
    else {
      setLoading(false);
      setError("Kylas is not configured: set REACT_APP_KYLAS_API_KEY in .env and restart.");
    }
  }, [load]);

  // Owners present on the loaded tickets, with counts — Kylas has no cheap "list users" call
  // for an api-key, and only owners that actually hold a ticket are worth offering.
  const ownerOptions = useMemo(() => {
    const byId = new Map<number, { name: string; count: number }>();
    for (const d of deals) {
      if (!d.ownedBy?.id) continue;
      const cur = byId.get(d.ownedBy.id) ?? { name: d.ownedBy.name ?? `User ${d.ownedBy.id}`, count: 0 };
      cur.count++;
      byId.set(d.ownedBy.id, cur);
    }
    return Array.from(byId, ([value, o]) => ({ value, label: `${o.name} (${o.count})`, name: o.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deals]);

  const visible = useMemo(() => {
    const floor = dateFloor(dateFilter);
    const q = query.trim().toUpperCase();
    return deals.filter(
      (d) =>
        (floor == null || (d.createdAt && new Date(d.createdAt).getTime() >= floor)) &&
        (!ownerIds.length || (d.ownedBy?.id != null && ownerIds.includes(d.ownedBy.id))) &&
        (!q ||
          d.name?.toUpperCase().includes(q) ||
          String(d.id).includes(q) ||
          d.associatedContacts?.some((c) => c.name?.toUpperCase().includes(q)))
    );
  }, [deals, query, dateFilter, ownerIds]);

  // Back to page 1 whenever the filtered set changes (search, date chip, refresh).
  useEffect(() => setPage(1), [visible]);

  const pageDeals = useMemo(() => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [visible, page]);

  const onAction = useCallback((dealId: number, type: ActionType, text: string, moved?: boolean) => {
    setComposer(null);
    if (moved) {
      // Now In Progress, so it no longer belongs on this list.
      setDeals((prev) => prev.filter((d) => d.id !== dealId));
      setSelected((prev) => (prev?.id === dealId ? null : prev));
      return;
    }
    setLastAction((prev) => ({ ...prev, [dealId]: { type, text } }));
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <AlertOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>Escalations</h2>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
        Kylas escalation tickets in <b>Internal Dependency</b> with Dependency <b>Passed to Procurement</b>. Notes and
        files you add here are saved on the Kylas deal.
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="Search by deal name, id or customer"
          style={{ width: 320 }}
          onSearch={setQuery}
          onChange={(e) => !e.target.value && setQuery("")}
        />
        <Select
          mode="multiple"
          allowClear
          showSearch
          maxTagCount="responsive"
          placeholder="All owners"
          style={{ minWidth: 220, maxWidth: 360 }}
          value={ownerIds}
          onChange={setOwnerIds}
          options={ownerOptions}
          optionFilterProp="name"
        />
        <Segmented value={dateFilter} onChange={(v) => setDateFilter(v as DateFilter)} options={DATE_OPTIONS} />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </Space>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      {!loading && !error && (
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
          {visible.length} ticket{visible.length !== 1 ? "s" : ""}
          {query ? ` · "${query}"` : ""}
        </Typography.Text>
      )}

      {loading ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} size="small">
              <Skeleton active paragraph={{ rows: 1 }} />
            </Card>
          ))}
        </Space>
      ) : !error && visible.length === 0 ? (
        <Empty description="No escalations passed to procurement" />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          {pageDeals.map((deal) => {
            const open = composer?.dealId === deal.id ? composer.type : null;
            const done = lastAction[deal.id];
            return (
              <Card
                key={deal.id}
                size="small"
                hoverable
                onClick={() => setSelected(deal)}
                style={{
                  borderLeft: "4px solid #faad14",
                  background: selected?.id === deal.id ? "#fffbe6" : undefined,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <Typography.Text strong ellipsis={{ tooltip: deal.name }} style={{ flex: 1, minWidth: 0 }}>
                    {deal.name}
                  </Typography.Text>
                  {deal.pipelineStage?.name && <Tag color="gold">{deal.pipelineStage.name.trim()}</Tag>}
                </div>
                <div style={{ fontSize: 12, color: "#8c8c8c", margin: "4px 0 8px", display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ color: "#ad6800", fontWeight: 500 }}>{deal.associatedContacts?.[0]?.name ?? "Customer"}</span>
                  {deal.ownedBy?.name && <span>· {deal.ownedBy.name}</span>}
                  <Tag icon={<ClockCircleOutlined />} style={{ margin: 0 }}>
                    {relativeAge(deal.createdAt)}
                  </Tag>
                  {typeof deal.customFieldValues?.cfRequestType !== "undefined" && (
                    <span>· {cfDisplay(deal.customFieldValues?.cfRequestType)}</span>
                  )}
                </div>
                <Space wrap onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="small"
                    icon={<FormOutlined />}
                    type={open === "update" ? "primary" : "default"}
                    onClick={() => setComposer(open === "update" ? null : { dealId: deal.id, type: "update" })}
                  >
                    Procurement update
                  </Button>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    type={open === "note" ? "primary" : "default"}
                    onClick={() => setComposer(open === "note" ? null : { dealId: deal.id, type: "note" })}
                  >
                    Add note
                  </Button>
                  <Button
                    size="small"
                    icon={<PaperClipOutlined />}
                    type={open === "doc" ? "primary" : "default"}
                    onClick={() => setComposer(open === "doc" ? null : { dealId: deal.id, type: "doc" })}
                  >
                    Upload doc
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    icon={<ExportOutlined />}
                    href={kylasDealUrl(deal.id)}
                    target="_blank"
                    className="text-decoration-none"
                  >
                    Open in Kylas
                  </Button>
                </Space>
                {open && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #f0f0f0", paddingTop: 10 }} onClick={(e) => e.stopPropagation()}>
                    {open === "update" ? (
                      <ProcurementUpdateForm dealId={deal.id} author={author} onDone={(t, m) => onAction(deal.id, "update", t, m)} />
                    ) : open === "note" ? (
                      <NoteComposer dealId={deal.id} author={author} onDone={(t) => onAction(deal.id, "note", t)} />
                    ) : (
                      <FileUploader dealId={deal.id} author={author} onDone={(t) => onAction(deal.id, "doc", t)} />
                    )}
                  </div>
                )}
                {done && (
                  <Alert
                    style={{ marginTop: 10 }}
                    type="success"
                    showIcon
                    message={done.type === "update" ? "Procurement update saved (stage unchanged)" : done.type === "note" ? "Note added" : "Document uploaded"}
                    description={<Typography.Text ellipsis>{done.text}</Typography.Text>}
                  />
                )}
              </Card>
            );
          })}
          {visible.length > PAGE_SIZE && (
            <Pagination
              current={page}
              pageSize={PAGE_SIZE}
              total={visible.length}
              onChange={(p) => {
                setPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              showSizeChanger={false}
              showTotal={(total, [from, to]) => `${from}–${to} of ${total}`}
              style={{ marginTop: 8, textAlign: "right" }}
            />
          )}
        </Space>
      )}

      <EscalationDrawer deal={selected} author={author} onClose={() => setSelected(null)} onAction={onAction} />
    </div>
  );
};

export default OmsEscalations;
